import {
  Document,
  Font,
  Image,
  Page,
  pdf,
  StyleSheet,
  Text,
  View,
} from '@react-pdf/renderer';
import { toCanvas } from 'bwip-js';
import PropTypes from 'prop-types';
import React from 'react';

Font.register({
  family: 'Inter',
  fonts: [
    { src: '/fonts/Inter/TTF/Inter-Regular.ttf' },
    { src: '/fonts/Inter/TTF/Inter-Bold.ttf', fontWeight: 'bold' },
  ],
});

// react-pdf hyphenates long words by default ("Hamilton" → "Hamil-ton");
// keep words whole and let lines break at spaces only.
Font.registerHyphenationCallback((word) => [word]);

// 2" x 1" label stock at 72pt/inch
export const LABEL_SIZE = [144, 72];

/** The label's form values — the one definition shared by all consumers. */
export const labelValuesShape = PropTypes.shape({
  name: PropTypes.string,
  sku: PropTypes.string,
  batch: PropTypes.string,
  qrHeader: PropTypes.string,
  qrValue: PropTypes.string,
});

const styles = StyleSheet.create({
  page: {
    flexDirection: 'row',
    padding: 6,
    fontFamily: 'Inter',
    backgroundColor: '#fff',
  },
  left: {
    flex: 1,
    flexDirection: 'column',
    paddingRight: 4,
  },
  logoBox: {
    backgroundColor: '#000',
    alignSelf: 'flex-start',
    padding: '4 8 2 2',
  },
  logoText: {
    color: '#fff',
    fontSize: 5,
    fontWeight: 'bold',
    letterSpacing: 2,
  },
  name: {
    fontSize: 8,
    fontWeight: 'bold',
    lineHeight: 1.1,
    marginTop: 3,
  },
  bottomRow: {
    marginTop: 'auto',
    paddingTop: 2,
    flexDirection: 'row',
    alignItems: 'flex-end',
  },
  // ~8.5mm on the printed label — comfortably scannable for a 22x22 DataMatrix.
  dataMatrix: {
    width: 24,
    height: 24,
    marginRight: 5,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    marginTop: 1,
  },
  rowKey: {
    width: 19,
    flexShrink: 0,
    fontSize: 4,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    paddingBottom: 0.5,
  },
  rowValue: {
    fontSize: 5.5,
    fontWeight: 'bold',
  },
  right: {
    maxWidth: 40,
    flexDirection: 'column',
    alignItems: 'center',
  },
  qrHeader: {
    fontSize: 6.5,
    fontWeight: 'bold',
    marginBottom: 2,
  },
  qr: {
    width: 38,
    height: 38,
  },
});

export default function LabelPdf({ values, qrDataUrl = null, dataMatrixDataUrl = null }) {
  const {
    name, sku, batch, qrHeader,
  } = values;
  const rows = [
    ['SKU', sku],
    ['BATCH', batch],
  ].filter(([, value]) => value);

  return (
    <Document>
      <Page size={LABEL_SIZE} style={styles.page}>
        <View style={styles.left}>
          <View style={styles.logoBox}>
            <Text style={styles.logoText}>PARALLEL</Text>
          </View>
          {name ? <Text style={styles.name}>{name}</Text> : null}
          <View style={styles.bottomRow}>
            {dataMatrixDataUrl ? <Image src={dataMatrixDataUrl} style={styles.dataMatrix} /> : null}
            <View>
              {rows.map(([key, value]) => (
                <View style={styles.row} key={key}>
                  <Text style={styles.rowKey}>{key}</Text>
                  <Text style={styles.rowValue}>{value}</Text>
                </View>
              ))}
            </View>
          </View>
        </View>
        {(qrHeader || qrDataUrl) ? (
          <View style={styles.right}>
            <Text style={styles.qrHeader}>{qrHeader || ' '}</Text>
            {qrDataUrl ? <Image src={qrDataUrl} style={styles.qr} /> : null}
          </View>
        ) : null}
      </Page>
    </Document>
  );
}

LabelPdf.propTypes = {
  values: labelValuesShape.isRequired,
  qrDataUrl: PropTypes.string,
  dataMatrixDataUrl: PropTypes.string,
};

/**
 * Encode text as a barcode PNG data URL via an offscreen canvas, because
 * react-pdf's <Image> wants a raster/data URL, not live canvas output.
 */
function symbolDataUrl(bcid, text) {
  const canvas = document.createElement('canvas');
  toCanvas(canvas, { bcid, text });
  return canvas.toDataURL();
}

/**
 * The single place a label PDF is produced: form values in, PDF blob out.
 * Used by both the live preview and the print handler.
 *
 * `travelerId` (when a traveler is loaded) is encoded as a DataMatrix in the
 * bottom-left corner — a bare UUID, not a URL, so it stays an opaque internal
 * traceability code while the customer-facing QR carries the public link.
 */
export async function renderLabelBlob(values, travelerId = null) {
  const qrDataUrl = values.qrValue ? symbolDataUrl('qrcode', values.qrValue) : null;
  const dataMatrixDataUrl = travelerId ? symbolDataUrl('datamatrix', travelerId) : null;
  return pdf(
    <LabelPdf values={values} qrDataUrl={qrDataUrl} dataMatrixDataUrl={dataMatrixDataUrl} />,
  ).toBlob();
}
